import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Rocket, Star, Bell, ArrowLeft, Presentation, Mic, Award } from 'lucide-react';

export default function ComingSoonPresentation() {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-green-100 flex flex-col">
      
      {/* Navbar - Compact */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                  alt="FaheemGPT Logo" 
                  className="w-10 h-10 rounded-lg object-cover" 
                />
            </Link>
            <span className="text-xl font-bold tracking-tight">FaheemGPT <span className="text-slate-400 font-normal">Intelligent Tutoring Platform</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/">
                <Button variant="ghost" className="flex gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center relative overflow-hidden px-6 py-20">
        
        {/* Background Decorations */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-green-50 rounded-full blur-3xl -z-10 opacity-50 animate-pulse-slow"></div>
        
        <div className="max-w-3xl mx-auto text-center space-y-8">
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none px-4 py-1.5 text-sm">
                <Rocket className="w-4 h-4 mr-2 inline" /> Launching Soon
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900">
                Presentation and <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600">Public Speaking Skills</span>
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
                Build confidence in public speaking and presentations. Master senior project defenses, class presentations, and learn to communicate your ideas effectively to any audience.
            </p>

            {/* Feature Preview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 text-left">
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-6 space-y-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                            <Presentation className="w-5 h-5" />
                        </div>
                        <h3 className="font-semibold text-slate-900">Presentation Skills</h3>
                        <p className="text-sm text-slate-500">Learn to structure, design, and deliver impactful presentations.</p>
                    </CardContent>
                </Card>
                
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-6 space-y-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center text-teal-600">
                            <Mic className="w-5 h-5" />
                        </div>
                        <h3 className="font-semibold text-slate-900">Public Speaking</h3>
                        <p className="text-sm text-slate-500">Practice speaking to audiences and overcome stage fright.</p>
                    </CardContent>
                </Card>
                
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-6 space-y-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                            <Award className="w-5 h-5" />
                        </div>
                        <h3 className="font-semibold text-slate-900">Project Defense</h3>
                        <p className="text-sm text-slate-500">Prepare for senior project presentations and thesis defenses.</p>
                    </CardContent>
                </Card>
            </div>

            {/* Notify Button */}
            <div className="pt-8">
                <Button size="lg" className="h-14 px-8 text-lg bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg">
                    <Bell className="w-5 h-5 mr-2" /> Notify Me When Available
                </Button>
            </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-8 h-8 rounded-lg object-cover" 
            />
             <span className="font-semibold text-slate-700">FaheemGPT Intelligent Tutoring Platform</span>
          </div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/support" className="hover:text-slate-900">Support</Link>
          </div>
          <div>
            &copy; 2024 FaheemGPT Inc. All rights reserved.
          </div>
        </div>
      </footer>

    </div>
  );
}
